// THE PUBLIC FACTS OF A RESULT TYPE — the columns of `data/tables/result-types.json` that a SCREEN needs
// (T-237, D-117; asked for by T-041 and again by T-056).
//
// The inference itself (`match`, `actionTypes`, table order as precedence) is ENGINE §5 and stays inside
// `src/engine/resultType.ts`, which the apps may not import (ARCH §8.1, ESLint-enforced). But three of that
// table's columns are not rules at all, they are facts a cell has to know:
//
//   `followsClick` — whether a conversion rate means anything for this type (ENGINE §3 N-3). Both apps
//                    carried a hand-written `CLICK_FOLLOWING_RESULTS` set beside it.
//   `per1000`      — the cost is per 1,000 of them (people reached), not per one.
//   `hasValue`     — `action_values` carries money for it, so value of results / return on ad spend exist.
//
// So this module reads the same file and exposes exactly those, beside `metricDefinition`. It deliberately
// does NOT re-validate the inference columns — `src/engine/resultType.ts` does that at load — and
// `resultTypes.test.ts` asserts the two readings agree row for row, so the two can never drift.

import table from '../../data/tables/result-types.json';
import { RESULT_NOUNS, type ResultNoun } from '../messages/resultNouns';
import type { ResultTypeKey } from '../types/entities';

/** What a screen may know about a result type. The inference columns are the engine's and are not here. */
export interface ResultTypeFacts {
  key: ResultTypeKey;
  /** The product's words: "Purchases" / "purchase" / "purchases" (REQ-043 AC2, REQ §7). */
  nouns: ResultNoun;
  /** Ads Manager's word, for "In Ads Manager this is called …"; empty for `mixed`. */
  metaTerm: string;
  /** `action_values` carries money for it — value of results and return on ad spend are real. */
  hasValue: boolean;
  /** A conversion rate (results ÷ link clicks) is meaningful (ENGINE §3 N-3). */
  followsClick: boolean;
  /** Counted from `reach`: cost per result is per 1,000 people (ENGINE §5 rule 5). */
  per1000: boolean;
  /**
   * The singular reads as a quantity, not a thing ("1,000 people reached"), so a sentence must not put an
   * article in front of it: "A result here is one 1,000 people reached." was the bug T-048 found.
   */
  needsArticle: boolean;
}

interface RawRow {
  key: string;
  metaTerm?: unknown;
  hasValue?: unknown;
  followsClick?: unknown;
  per1000?: unknown;
}

function factsOf(raw: RawRow): ResultTypeFacts {
  const key = raw.key as ResultTypeKey;
  const nouns = RESULT_NOUNS[key];
  if (!nouns) throw new Error(`result-types.json: "${raw.key}" has no result noun`);
  for (const flag of ['hasValue', 'followsClick', 'per1000'] as const)
    if (typeof raw[flag] !== 'boolean')
      throw new Error(`result-types.json: "${raw.key}".${flag} must be a boolean`);
  if (typeof raw.metaTerm !== 'string')
    throw new Error(`result-types.json: "${raw.key}".metaTerm must be a string`);
  return {
    key,
    nouns,
    metaTerm: raw.metaTerm,
    hasValue: raw.hasValue as boolean,
    followsClick: raw.followsClick as boolean,
    per1000: raw.per1000 as boolean,
    // A singular that starts with a digit is a quantity ("1,000 people reached"), never "a …".
    needsArticle: !/^\d/.test(nouns.singular),
  };
}

const FACTS: ReadonlyMap<ResultTypeKey, ResultTypeFacts> = new Map(
  (table.types as RawRow[]).map((raw) => {
    const facts = factsOf(raw);
    return [facts.key, facts] as const;
  }),
);

/** Every result type the product names, in table order (precedence order — see ENGINE §5). */
export const RESULT_TYPE_FACTS: readonly ResultTypeFacts[] = [...FACTS.values()];

/** The public facts of one result type. Throws on an unknown key — a typo is a bug, not a missing row. */
export function resultTypeDefinition(key: ResultTypeKey): ResultTypeFacts {
  const facts = FACTS.get(key);
  if (!facts) throw new Error(`result-types.json has no row for "${String(key)}"`);
  return facts;
}

/**
 * Result types whose results follow a click, so a conversion rate means something. Was an app-side copy in
 * `apps/web/src/lists/rowModel.ts` and again in `apps/mobile/src/lists/rowModel.ts`; it is the table now.
 */
export const CLICK_FOLLOWING_RESULTS: ReadonlySet<ResultTypeKey> = new Set(
  RESULT_TYPE_FACTS.filter((f) => f.followsClick).map((f) => f.key),
);
